/**
 * Toast model — pure types + constants for the toast system (E10-F02-T03).
 *
 * Parity with the legacy `core.js → showNotification(message, type = 'success')`
 * + the static `#toast-container`. The live app only ever raised two kinds of
 * toast: `success` (the default — green, fa-check-circle) and `error` (red,
 * fa-exclamation-circle). We keep that exact contract as the toast `type` union
 * so the React port is a drop-in replacement for every `core.showNotification`
 * call site that lands in later feature tasks.
 *
 * This file is intentionally framework-free (no React) so the timing/visual
 * constants and the type union can be unit-tested and reused by both the
 * provider (state + timers) and the presentational container (chrome).
 */

/**
 * Toast kind. Mirrors the legacy `type` argument 1:1 — `success` is the default
 * (the legacy signature defaulted it), `error` is the only other value any
 * call site passed.
 */
export type ToastType = 'success' | 'error';

/** A live toast in the stack. */
export interface Toast {
  /** Stable unique id (stack key + dismissal target). */
  readonly id: string;
  /** Visible message. Rendered as a React text node → intrinsically escaped. */
  readonly message: string;
  /** Visual variant (colour + icon). */
  readonly type: ToastType;
  /**
   * Auto-dismiss delay in ms. The provider arms a timer for this long, then
   * plays the exit animation and removes the toast. `0` (or negative) means the
   * toast is sticky (no auto-dismiss) — handy for errors a caller wants to pin.
   */
  readonly duration: number;
}

/**
 * Default auto-dismiss delay (ms). Parity with the legacy `setTimeout(…, 3000)`
 * before the reverse animation.
 */
export const TOAST_DEFAULT_DURATION = 3000;

/**
 * Default auto-dismiss delay (ms) for ERROR toasts (E06-F01-T15). Longer than
 * the success default so a failure is readable, but never sticky: a pinned red
 * toast sat over Save draft / Run research for 15+ minutes. Acceptance: an error
 * toast leaves on its own within 10 seconds.
 */
export const TOAST_ERROR_DURATION = 8000;

/**
 * Grace window (ms) for the screen-change sweep (E06-F01-T15): when the screen
 * changes, toasts raised more than this long ago are dismissed (they belong to
 * the previous screen), while a toast raised by the very action that navigated
 * (e.g. «Research request created» → Run screen) survives the switch.
 */
export const TOAST_SCREEN_GRACE_MS = 1000;

/**
 * Exit-animation duration (ms): after the dismiss timer fires we add the
 * leave classes and wait this long before unmounting, matching the legacy
 * `setTimeout(() => toast.remove(), 300)` that followed the `transition-all
 * duration-300` reverse animation.
 */
export const TOAST_EXIT_MS = 300;

/** Per-type background utility (parity: success=green, error=red). */
export const TOAST_BG_CLASS: Record<ToastType, string> = {
  success: 'bg-green-600',
  error: 'bg-red-600',
};

/** Per-type Font Awesome icon (parity: fa-check-circle / fa-exclamation-circle). */
export const TOAST_ICON_CLASS: Record<ToastType, string> = {
  success: 'fa-check-circle',
  error: 'fa-exclamation-circle',
};

/** Options accepted when raising a toast. */
export interface ToastOptions {
  /** Override the auto-dismiss delay (ms). `0` → sticky. */
  readonly duration?: number;
}

let counter = 0;

/**
 * Generate a process-unique toast id. We avoid `crypto.randomUUID()` so the
 * model stays usable in any environment (and the ids stay short + readable in
 * the DOM / tests). A monotonic counter combined with `Date.now()` is unique
 * enough for a transient UI stack.
 */
export function createToastId(): string {
  counter += 1;
  return `toast-${Date.now().toString(36)}-${counter.toString(36)}`;
}

/**
 * Build a {@link Toast} from a raise request. Pure: no side effects, no timers —
 * the provider owns those. Resolves the duration:
 *   - an explicit `options.duration` (incl. `0`) always wins;
 *   - otherwise `error` toasts default to {@link TOAST_ERROR_DURATION} (8s —
 *     long enough to read, still click-to-close earlier), while `success` toasts
 *     keep the legacy 3000ms auto-dismiss. Errors used to default to sticky,
 *     which left a red toast covering the form actions indefinitely
 *     (E06-F01-T15); a caller that truly needs to pin passes `duration: 0`.
 */
export function makeToast(
  message: string,
  type: ToastType = 'success',
  options: ToastOptions = {},
): Toast {
  const defaultDuration = type === 'error' ? TOAST_ERROR_DURATION : TOAST_DEFAULT_DURATION;
  return {
    id: createToastId(),
    message,
    type,
    duration: options.duration ?? defaultDuration,
  };
}
